/**
 * WarrantyEvidenceRulesPage — admin CRUD for the warranty AI agent's
 * rule book.  Each rule maps a defect category (e.g. "oil_leak") to:
 *   * keywords (fast pre-filter for the AI)
 *   * AI instructions (free text the agent uses to coach the user)
 *   * minimum evidence (count + kind of media required)
 *
 * Editable by super-admin or anyone with
 * `can_manage_warranty_evidence_rules` (e.g. aftersales-admin / R&D).
 */
import { useEffect, useState } from "react";
import { Wrench, Plus, FloppyDisk, Trash, X, Lightbulb, Camera, VideoCamera, FilePdf, Envelope, PaperPlaneTilt, CheckCircle, XCircle, HandPalm } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import AdminPageHeader from "@/components/AdminPageHeader";
import EvidenceRulesTour from "@/components/EvidenceRulesTour";

const KIND_OPTIONS = [
  { v: "photo", label: "Photo", Icon: Camera },
  { v: "video", label: "Video", Icon: VideoCamera },
  { v: "file",  label: "File",  Icon: FilePdf },
];

const EMPTY_RULE = {
  category_key: "",
  label: "",
  description: "",
  keywords: [],
  ai_instructions: "",
  min_evidence: [],
  active: true,
};

export default function WarrantyEvidenceRulesPage({ user, onLogout, onLogoutAll }) {
  const isAdmin = !!(user?.is_admin || user?.is_super_admin);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // active draft (rule object)
  const [busy, setBusy] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  // iter150 — email + dealer-proposal state
  const [emailing, setEmailing] = useState(null);   // {rule_ids:[], to:""} or null
  const [proposing, setProposing] = useState(null); // proposal draft or null
  const [pendingProposals, setPendingProposals] = useState([]);
  const [showProposals, setShowProposals] = useState(false);
  const [myProposals, setMyProposals] = useState([]);
  const [showMyProposals, setShowMyProposals] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/warranty/evidence-rules");
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load rules");
    } finally { setLoading(false); }
  };

  const loadPendingProposals = async () => {
    if (!isAdmin) return;
    try {
      const r = await api.get("/admin/warranty/evidence-rules/proposals?status=pending");
      setPendingProposals(r.data?.items || []);
    } catch (e) {
      // 403 if no perm — silently ignore for admins without warranty rights
      if (e?.response?.status !== 403) {
        toast.error(e?.response?.data?.detail || "Failed to load proposals");
      }
    }
  };

  const loadMyProposals = async () => {
    try {
      const r = await api.get("/warranty/evidence-rules/proposals/mine");
      setMyProposals(r.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load your proposals");
    }
  };

  const loadSuggestions = async () => {
    try {
      const r = await api.get("/admin/warranty/evidence-rules/_suggestions/keywords");
      setSuggestions(r.data?.items || []);
      setShowSuggestions(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load suggestions");
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { if (isAdmin) loadPendingProposals(); }, [isAdmin]);

  // iter144b — pick up a one-shot prefill blob dropped by
  // WarrantyClaimChatPage's "Make rule" action. Open the New Rule modal
  // pre-filled with the message body as ai_instructions, then clear the
  // blob so a refresh doesn't re-trigger the modal.
  useEffect(() => {
    let prefill = null;
    try {
      const raw = window.sessionStorage.getItem("__warranty_rule_prefill");
      if (raw) {
        prefill = JSON.parse(raw);
        window.sessionStorage.removeItem("__warranty_rule_prefill");
      }
    } catch { /* sessionStorage / JSON parse failed — silently skip */ }
    if (prefill && (prefill.ai_instructions || prefill.keywords)) {
      setEditing({
        ...EMPTY_RULE,
        ai_instructions: prefill.ai_instructions || "",
        keywords: prefill.keywords || [],
        _prefill_source: prefill.source || null,
      });
      toast.info("Pre-filled from chat — review and add a category key + label.");
    }
  }, []);

  const startNew = () => setEditing({ ...EMPTY_RULE });
  const startEdit = (rule) => setEditing({ ...rule, _existing: true });
  const cancelEdit = () => setEditing(null);

  const save = async () => {
    if (!editing) return;
    if (!editing.category_key || editing.category_key.length < 2) {
      toast.error("Category key needs at least 2 characters."); return;
    }
    if (!editing.label || editing.label.length < 2) {
      toast.error("Label needs at least 2 characters."); return;
    }
    setBusy(true);
    try {
      const body = {
        category_key: editing.category_key.trim().toLowerCase().replace(/\s+/g, "_"),
        label: editing.label.trim(),
        description: editing.description || "",
        keywords: (editing.keywords || []).map((k) => k.trim()).filter(Boolean),
        ai_instructions: editing.ai_instructions || "",
        min_evidence: (editing.min_evidence || []).map((m) => ({
          kind: m.kind, count: Math.max(1, parseInt(m.count) || 1),
          label: m.label || "", instructions: m.instructions || "",
        })),
        active: !!editing.active,
      };
      if (editing._existing) {
        await api.put(`/admin/warranty/evidence-rules/${encodeURIComponent(editing.rule_id)}`, body);
        toast.success("Rule updated");
      } else {
        await api.post("/admin/warranty/evidence-rules", body);
        toast.success("Rule created");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const remove = async (rule) => {
    if (!window.confirm(`Delete rule "${rule.label}"? Existing claims keep their snapshot.`)) return;
    try {
      await api.delete(`/admin/warranty/evidence-rules/${encodeURIComponent(rule.rule_id)}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  // iter150 — Email a single rule or all active rules.
  const openEmailDialog = (ruleIds) => {
    setEmailing({ rule_ids: ruleIds || [], to: (user?.email || "").toLowerCase() });
  };

  const sendEmail = async () => {
    if (!emailing) return;
    setBusy(true);
    try {
      const body = {};
      if (emailing.rule_ids && emailing.rule_ids.length > 0) body.rule_ids = emailing.rule_ids;
      if (emailing.to && emailing.to.trim()) body.to_email = emailing.to.trim();
      const r = await api.post("/warranty/evidence-rules/email", body);
      const data = r.data || {};
      toast.success(`Sent ${data.rule_count || "0"} rule(s) to ${data.to} (${data.status})`);
      setEmailing(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Email failed");
    } finally { setBusy(false); }
  };

  // iter150 — Submit a new dealer proposal.
  const startPropose = () => {
    setProposing({
      label: "", description: "", keywords: [], ai_instructions: "",
      min_evidence: [], rationale: "", category_key: "",
    });
  };

  const submitProposal = async () => {
    if (!proposing) return;
    if (!proposing.label || proposing.label.trim().length < 2) {
      toast.error("Label needs at least 2 characters."); return;
    }
    setBusy(true);
    try {
      await api.post("/warranty/evidence-rules/propose", {
        label: proposing.label.trim(),
        description: proposing.description || "",
        keywords: (proposing.keywords || []).map((k) => k.trim()).filter(Boolean),
        ai_instructions: proposing.ai_instructions || "",
        min_evidence: (proposing.min_evidence || []).map((m) => ({
          kind: m.kind, count: Math.max(1, parseInt(m.count) || 1),
          label: m.label || "", instructions: m.instructions || "",
        })),
        rationale: proposing.rationale || "",
        category_key: (proposing.category_key || "").trim() || undefined,
      });
      toast.success("Proposal submitted — a service admin will review it.");
      setProposing(null);
      // refresh proposer's "My proposals" list so the new one is visible.
      if (showMyProposals) loadMyProposals();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Proposal failed");
    } finally { setBusy(false); }
  };

  // iter150 — Admin approve / reject.
  const approveProposal = async (p) => {
    const cat = window.prompt(
      `Set category_key for the new rule (leave blank to auto-generate):`,
      p.category_key || "");
    if (cat === null) return; // cancelled
    try {
      await api.post(
        `/admin/warranty/evidence-rules/proposals/${encodeURIComponent(p.proposal_id)}/approve`,
        { category_key: cat || undefined, reason: "", notify_proposer: true });
      toast.success(`Approved — new rule created from "${p.label}"`);
      loadPendingProposals();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    }
  };

  const rejectProposal = async (p) => {
    const reason = window.prompt(`Reason for rejecting "${p.label}" (optional):`, "");
    if (reason === null) return;
    try {
      await api.post(
        `/admin/warranty/evidence-rules/proposals/${encodeURIComponent(p.proposal_id)}/reject`,
        { reason: reason || "", notify_proposer: true });
      toast.info("Proposal rejected — proposer notified.");
      loadPendingProposals();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reject failed");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 font-['Lato'] text-slate-900" data-testid="evidence-rules-page">
      <AdminPageHeader
        sticky maxWidth="max-w-5xl" testId="evidence-rules-back"
        icon={<Wrench size={20} weight="fill" className="text-amber-600" />}
        title="Warranty evidence rules"
        subtitle="Per defect category: minimum supporting media + AI coaching instructions."
        user={user} onLogout={onLogout} onLogoutAll={onLogoutAll}
      />
      <main className="max-w-5xl mx-auto p-6 space-y-5">
        <EvidenceRulesTour />

        {!isAdmin && (
          <div className="bg-blue-50 border border-blue-200 rounded-sm px-3 py-2 text-[12px] text-blue-900" data-testid="evidence-rules-readonly-banner">
            <strong>Read-only view.</strong> These rules are shared with everyone. You can email them to yourself or a teammate, or submit your own rule proposal — a service admin from US Air Compressor / Air Compressor Canada will review it.
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-slate-600">
            {loading ? "Loading…" : `${items.length} rule${items.length === 1 ? "" : "s"} configured`}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={() => openEmailDialog([])} data-testid="evidence-rule-email-all"
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-wider rounded-sm border border-blue-300 text-blue-800 hover:bg-blue-50">
              <Envelope size={11} weight="bold" /> Email all rules
            </button>
            <button onClick={startPropose} data-testid="evidence-rule-propose"
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-wider rounded-sm border border-emerald-300 text-emerald-800 hover:bg-emerald-50">
              <HandPalm size={11} weight="bold" /> Propose a rule
            </button>
            <button onClick={() => { if (!showMyProposals) loadMyProposals(); setShowMyProposals((v) => !v); }}
                    data-testid="evidence-rule-my-proposals-toggle"
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-wider rounded-sm border border-slate-300 text-slate-700 hover:bg-slate-50">
              My proposals
            </button>
            {isAdmin && (
              <>
                <button onClick={() => { loadPendingProposals(); setShowProposals((v) => !v); }}
                        data-testid="evidence-rule-pending-proposals-toggle"
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-wider rounded-sm border border-amber-400 text-amber-800 hover:bg-amber-50">
                  Pending proposals
                  {pendingProposals.length > 0 && (
                    <span data-testid="evidence-rule-pending-badge"
                          className="ml-1 inline-flex items-center justify-center text-[10px] font-bold rounded-full bg-amber-500 text-white w-5 h-5">
                      {pendingProposals.length}
                    </span>
                  )}
                </button>
                <button onClick={loadSuggestions} data-testid="evidence-rule-suggestions"
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-wider rounded-sm border border-violet-300 text-violet-800 hover:bg-violet-50">
                  <Lightbulb size={11} weight="bold" /> Mine claim logs
                </button>
                <button onClick={startNew} data-testid="evidence-rule-new"
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-wider rounded-sm bg-amber-500 text-white hover:bg-amber-600">
                  <Plus size={11} weight="bold" /> New rule
                </button>
              </>
            )}
          </div>
        </div>

        {/* iter150 — My proposals panel (any signed-in user) */}
        {showMyProposals && (
          <section className="bg-slate-50 border border-slate-200 rounded-md p-3" data-testid="evidence-rule-my-proposals-panel">
            <div className="flex items-center gap-2 mb-2">
              <HandPalm size={13} weight="fill" className="text-slate-700" />
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900">
                My proposals ({myProposals.length})
              </h3>
              <button onClick={() => setShowMyProposals(false)}
                      data-testid="evidence-rule-my-proposals-close"
                      className="ml-auto text-[10px] text-slate-600 hover:text-slate-900">
                <X size={11} weight="bold" />
              </button>
            </div>
            {myProposals.length === 0 ? (
              <p className="text-[11px] italic text-slate-600">You haven't submitted any proposals yet.</p>
            ) : (
              <ul className="space-y-1.5">
                {myProposals.map((p) => (
                  <li key={p.proposal_id} className="flex items-center gap-2 bg-white border border-slate-200 rounded-sm px-2 py-1.5"
                      data-testid={`evidence-rule-my-proposal-${p.proposal_id}`}>
                    <span className="text-xs font-bold flex-1 truncate">{p.label}</span>
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${
                      p.status === "approved" ? "bg-emerald-100 text-emerald-800"
                      : p.status === "rejected" ? "bg-rose-100 text-rose-800"
                      : "bg-amber-100 text-amber-800"
                    }`}>{p.status}</span>
                    {p.decision_reason && <span className="text-[10px] text-slate-500 italic truncate max-w-xs" title={p.decision_reason}>"{p.decision_reason}"</span>}
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        {/* iter150 — Pending proposals panel (admin only) */}
        {isAdmin && showProposals && (
          <section className="bg-amber-50 border border-amber-200 rounded-md p-3" data-testid="evidence-rule-pending-proposals-panel">
            <div className="flex items-center gap-2 mb-2">
              <HandPalm size={13} weight="fill" className="text-amber-700" />
              <h3 className="text-xs font-bold uppercase tracking-wider text-amber-900">
                Pending proposals ({pendingProposals.length})
              </h3>
              <button onClick={() => setShowProposals(false)}
                      data-testid="evidence-rule-pending-proposals-close"
                      className="ml-auto text-[10px] text-amber-700 hover:text-amber-900">
                <X size={11} weight="bold" />
              </button>
            </div>
            {pendingProposals.length === 0 ? (
              <p className="text-[11px] italic text-amber-800">No proposals waiting for review.</p>
            ) : (
              <ul className="space-y-2">
                {pendingProposals.map((p) => (
                  <li key={p.proposal_id} className="bg-white border border-amber-200 rounded-sm p-2"
                      data-testid={`evidence-rule-pending-${p.proposal_id}`}>
                    <div className="flex items-baseline gap-2 mb-1">
                      <span className="text-sm font-bold">{p.label}</span>
                      <code className="text-[10px] font-mono text-slate-500">{p.category_key || "(auto)"}</code>
                      <span className="ml-auto text-[10px] text-slate-500">by {p.proposed_by}</span>
                    </div>
                    {p.description && <p className="text-[11px] text-slate-700 mb-1">{p.description}</p>}
                    {p.rationale && <p className="text-[11px] italic text-slate-500 mb-1">Rationale: {p.rationale}</p>}
                    {p.ai_instructions && (
                      <div className="bg-amber-50 border-l-2 border-amber-400 px-2 py-1 text-[11px] text-amber-900 mb-1">
                        <Lightbulb size={10} weight="fill" className="inline mr-1" />{p.ai_instructions}
                      </div>
                    )}
                    {(p.keywords || []).length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-1">
                        {p.keywords.map((k) => (
                          <span key={k} className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-blue-50 text-blue-800 border border-blue-200">{k}</span>
                        ))}
                      </div>
                    )}
                    {(p.min_evidence || []).length > 0 && (
                      <ul className="text-[10px] text-slate-600 mb-1">
                        {p.min_evidence.map((ev, i) => (
                          <li key={i}>• {ev.count}× {ev.kind}{ev.label ? ` — ${ev.label}` : ""}</li>
                        ))}
                      </ul>
                    )}
                    <div className="flex items-center gap-2 mt-1">
                      <button onClick={() => approveProposal(p)}
                              data-testid={`evidence-rule-pending-approve-${p.proposal_id}`}
                              className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded-sm bg-emerald-600 text-white hover:bg-emerald-700">
                        <CheckCircle size={11} weight="fill" /> Approve
                      </button>
                      <button onClick={() => rejectProposal(p)}
                              data-testid={`evidence-rule-pending-reject-${p.proposal_id}`}
                              className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded-sm border border-rose-300 text-rose-700 hover:bg-rose-50">
                        <XCircle size={11} weight="fill" /> Reject
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}


        {showSuggestions && (
          <section className="bg-violet-50 border border-violet-200 rounded-md p-3" data-testid="evidence-rule-suggestions-panel">
            <div className="flex items-center gap-2 mb-2">
              <Lightbulb size={13} weight="fill" className="text-violet-700" />
              <h3 className="text-xs font-bold uppercase tracking-wider text-violet-900">
                Frequent terms not yet covered (last 30 days)
              </h3>
              <button onClick={() => setShowSuggestions(false)}
                      data-testid="evidence-rule-suggestions-close"
                      className="ml-auto text-[10px] text-violet-700 hover:text-violet-900">
                <X size={11} weight="bold" />
              </button>
            </div>
            {suggestions.length === 0 ? (
              <p className="text-[11px] italic text-violet-800">No new keywords yet — your existing rules already cover the common ones, or the agent hasn't run on enough recent claims.</p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {suggestions.map((s) => (
                  <button key={s.keyword}
                          data-testid={`evidence-rule-suggestion-${s.keyword}`}
                          onClick={() => {
                            startNew();
                            setEditing((d) => ({ ...(d || EMPTY_RULE), keywords: [s.keyword] }));
                          }}
                          className="text-[11px] px-2 py-0.5 rounded-sm bg-white border border-violet-300 text-violet-900 hover:bg-violet-100">
                    {s.keyword} <span className="text-violet-500 font-mono">×{s.count}</span>
                  </button>
                ))}
              </div>
            )}
          </section>
        )}

        {!loading && items.length === 0 && !editing && (
          <p className="text-center text-sm text-slate-500 italic py-12" data-testid="evidence-rules-empty">
            {isAdmin ? "No rules yet — click \"New rule\" to add the first one." : "No rules published yet — check back later, or propose one with the \"Propose a rule\" button above."}
          </p>
        )}

        <ul className="space-y-3" data-testid="evidence-rules-list">
          {items.map((it) => (
            <li key={it.rule_id} className="bg-white border border-slate-200 rounded-md p-4 shadow-sm"
                data-testid={`evidence-rule-${it.rule_id}`}>
              <div className="flex flex-wrap items-baseline gap-2 mb-1">
                <code className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-700">
                  {it.category_key}
                </code>
                {it.active === false && <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-600">Inactive</span>}
                <h3 className="text-sm font-bold">{it.label}</h3>
                <span className="text-[10px] text-slate-400 ml-auto">
                  {(it.min_evidence || []).reduce((a, b) => a + (b.count || 1), 0)} required item(s)
                </span>
              </div>
              {it.description && <p className="text-xs text-slate-700 leading-relaxed mb-2">{it.description}</p>}
              {(it.keywords || []).length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {it.keywords.map((k) => (
                    <span key={k} className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-blue-50 text-blue-800 border border-blue-200">
                      {k}
                    </span>
                  ))}
                </div>
              )}
              {it.ai_instructions && (
                <div className="mb-2 bg-amber-50 border-l-2 border-amber-400 px-3 py-1.5 text-[11px] text-amber-900">
                  <Lightbulb size={11} weight="fill" className="inline mr-1" />
                  <span className="italic">{it.ai_instructions}</span>
                </div>
              )}
              {(it.min_evidence || []).length > 0 && (
                <ul className="text-[11px] text-slate-700 space-y-0.5 mt-1">
                  {it.min_evidence.map((ev, i) => {
                    const O = KIND_OPTIONS.find((o) => o.v === ev.kind);
                    const Icon = O?.Icon || Camera;
                    return (
                      <li key={i} className="flex items-start gap-1.5">
                        <Icon size={11} weight="fill" className="text-slate-500 mt-0.5" />
                        <span><strong>{ev.count}× {ev.label || ev.kind}</strong>{ev.instructions ? ` — ${ev.instructions}` : ""}</span>
                      </li>
                    );
                  })}
                </ul>
              )}
              <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2 flex-wrap">
                <button onClick={() => openEmailDialog([it.rule_id])}
                        data-testid={`evidence-rule-email-${it.rule_id}`}
                        className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider border border-blue-300 text-blue-800 rounded-sm hover:bg-blue-50">
                  <Envelope size={11} weight="bold" className="inline mr-1" /> Email
                </button>
                {isAdmin && (
                  <>
                    <button onClick={() => startEdit(it)} data-testid={`evidence-rule-edit-${it.rule_id}`}
                            className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider border border-slate-200 rounded-sm hover:bg-slate-50">
                      Edit
                    </button>
                    <button onClick={() => remove(it)} data-testid={`evidence-rule-delete-${it.rule_id}`}
                            className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider border border-rose-200 text-rose-700 rounded-sm hover:bg-rose-50">
                      <Trash size={11} weight="fill" className="inline mr-1" /> Delete
                    </button>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>

        {editing && (
          <div className="fixed inset-0 z-[400] flex items-start justify-center bg-black/40 p-4 pt-12 overflow-y-auto"
               data-testid="evidence-rule-modal" onClick={() => !busy && cancelEdit()}>
            <div className="bg-white border border-slate-200 rounded-md shadow-xl w-full max-w-2xl"
                 onClick={(e) => e.stopPropagation()}>
              <header className="flex items-center gap-2 px-5 py-3 border-b border-slate-200">
                <Wrench size={16} weight="fill" className="text-amber-600" />
                <h2 className="text-sm font-bold tracking-tight">
                  {editing._existing ? `Edit rule · ${editing.category_key}` : "New evidence rule"}
                </h2>
                <button onClick={cancelEdit} disabled={busy}
                        data-testid="evidence-rule-modal-close"
                        className="ml-auto text-slate-400 hover:text-slate-700">
                  <X size={14} weight="bold" />
                </button>
              </header>
              <div className="p-5 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Category key" hint="lowercase, snake_case, used internally">
                    <input
                      type="text"
                      value={editing.category_key}
                      disabled={editing._existing}
                      onChange={(e) => setEditing({ ...editing, category_key: e.target.value })}
                      data-testid="evidence-rule-category"
                      placeholder="oil_leak"
                      className="w-full px-3 py-2 text-sm border border-slate-300 rounded-sm focus:border-blue-500 focus:outline-none disabled:bg-slate-100"
                    />
                  </Field>
                  <Field label="Display label">
                    <input
                      type="text"
                      value={editing.label}
                      onChange={(e) => setEditing({ ...editing, label: e.target.value })}
                      data-testid="evidence-rule-label"
                      placeholder="Oil leak"
                      className="w-full px-3 py-2 text-sm border border-slate-300 rounded-sm focus:border-blue-500 focus:outline-none"
                    />
                  </Field>
                </div>
                <Field label="Description (optional)">
                  <textarea
                    rows={2}
                    value={editing.description}
                    onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                    data-testid="evidence-rule-description"
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-sm focus:border-blue-500 focus:outline-none resize-y"
                  />
                </Field>
                <Field label="Keywords" hint="comma-separated, lowercase. Used for fast pre-filter before AI semantic check.">
                  <input
                    type="text"
                    value={(editing.keywords || []).join(", ")}
                    onChange={(e) => setEditing({ ...editing, keywords: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                    data-testid="evidence-rule-keywords"
                    placeholder="oil leak, oil drip, leaking oil, huile"
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-sm focus:border-blue-500 focus:outline-none"
                  />
                </Field>
                <Field label="AI instructions" hint="What should the AI agent tell the user when this rule fires?">
                  <textarea
                    rows={3}
                    value={editing.ai_instructions}
                    onChange={(e) => setEditing({ ...editing, ai_instructions: e.target.value })}
                    data-testid="evidence-rule-ai-instructions"
                    placeholder="If there is an oil leak or air leak, take a video of the leak showing where it leaks. If the air leak is not visible, apply soap-water and record again to make it visible."
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-sm focus:border-blue-500 focus:outline-none resize-y"
                  />
                </Field>
                <Field label="Required evidence" hint="Add one row per item the user must capture.">
                  <div className="space-y-2" data-testid="evidence-rule-min-evidence">
                    {(editing.min_evidence || []).map((ev, i) => (
                      <div key={i} className="flex flex-wrap items-start gap-2 bg-slate-50 border border-slate-200 rounded-sm p-2">
                        <select
                          value={ev.kind}
                          onChange={(e) => {
                            const m = [...editing.min_evidence];
                            m[i] = { ...m[i], kind: e.target.value };
                            setEditing({ ...editing, min_evidence: m });
                          }}
                          data-testid={`evidence-rule-kind-${i}`}
                          className="px-2 py-1 text-xs border border-slate-300 rounded-sm">
                          {KIND_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                        </select>
                        <input
                          type="number"
                          min={1} max={10}
                          value={ev.count}
                          onChange={(e) => {
                            const m = [...editing.min_evidence];
                            m[i] = { ...m[i], count: e.target.value };
                            setEditing({ ...editing, min_evidence: m });
                          }}
                          data-testid={`evidence-rule-count-${i}`}
                          className="w-14 px-2 py-1 text-xs border border-slate-300 rounded-sm"
                        />
                        <input
                          type="text"
                          value={ev.label}
                          placeholder="Label (e.g. Video of leak source)"
                          onChange={(e) => {
                            const m = [...editing.min_evidence];
                            m[i] = { ...m[i], label: e.target.value };
                            setEditing({ ...editing, min_evidence: m });
                          }}
                          data-testid={`evidence-rule-itemlabel-${i}`}
                          className="flex-1 min-w-[180px] px-2 py-1 text-xs border border-slate-300 rounded-sm"
                        />
                        <input
                          type="text"
                          value={ev.instructions}
                          placeholder="Instructions"
                          onChange={(e) => {
                            const m = [...editing.min_evidence];
                            m[i] = { ...m[i], instructions: e.target.value };
                            setEditing({ ...editing, min_evidence: m });
                          }}
                          data-testid={`evidence-rule-iteminstructions-${i}`}
                          className="flex-1 min-w-[200px] px-2 py-1 text-xs border border-slate-300 rounded-sm"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const m = [...editing.min_evidence];
                            m.splice(i, 1);
                            setEditing({ ...editing, min_evidence: m });
                          }}
                          data-testid={`evidence-rule-itemdelete-${i}`}
                          className="px-2 py-1 text-[10px] text-rose-700 hover:bg-rose-50 rounded-sm">
                          <Trash size={11} weight="fill" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => setEditing({
                        ...editing,
                        min_evidence: [...(editing.min_evidence || []),
                                       { kind: "video", count: 1, label: "", instructions: "" }],
                      })}
                      data-testid="evidence-rule-additem"
                      className="px-2 py-1 text-[11px] font-bold uppercase tracking-wider border border-amber-300 text-amber-800 rounded-sm hover:bg-amber-50">
                      <Plus size={11} weight="bold" className="inline mr-1" /> Add evidence item
                    </button>
                  </div>
                </Field>
                <label className="inline-flex items-center gap-2 text-xs text-slate-700">
                  <input
                    type="checkbox"
                    checked={!!editing.active}
                    onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                    data-testid="evidence-rule-active"
                  />
                  Active (rule fires on matching claims)
                </label>
              </div>
              <footer className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50">
                <button onClick={cancelEdit} disabled={busy}
                        className="px-3 py-2 text-xs font-bold uppercase tracking-wider border border-slate-200 rounded-sm hover:bg-white">
                  Cancel
                </button>
                <button onClick={save} disabled={busy}
                        data-testid="evidence-rule-save"
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-wider rounded-sm bg-blue-700 text-white hover:bg-blue-800 disabled:opacity-50">
                  <FloppyDisk size={11} weight="fill" />
                  {busy ? "Saving…" : (editing._existing ? "Save changes" : "Create rule")}
                </button>
              </footer>
            </div>
          </div>
        )}

        {/* iter150 — Email rule(s) modal */}
        {emailing && (
          <div className="fixed inset-0 z-[400] flex items-start justify-center bg-black/40 p-4 pt-24 overflow-y-auto"
               data-testid="evidence-rule-email-modal" onClick={() => !busy && setEmailing(null)}>
            <div className="bg-white border border-slate-200 rounded-md shadow-xl w-full max-w-md"
                 onClick={(e) => e.stopPropagation()}>
              <header className="flex items-center gap-2 px-5 py-3 border-b border-slate-200">
                <Envelope size={16} weight="fill" className="text-blue-700" />
                <h2 className="text-sm font-bold tracking-tight">
                  {emailing.rule_ids.length === 0 ? "Email all active rules" :
                   emailing.rule_ids.length === 1 ? "Email this rule" :
                   `Email ${emailing.rule_ids.length} rules`}
                </h2>
                <button onClick={() => setEmailing(null)} disabled={busy}
                        data-testid="evidence-rule-email-modal-close"
                        className="ml-auto text-slate-400 hover:text-slate-700">
                  <X size={14} weight="bold" />
                </button>
              </header>
              <div className="p-5 space-y-3">
                <Field label="Recipient" hint="Defaults to your own email. Edit to send to a teammate.">
                  <input
                    type="email"
                    value={emailing.to}
                    onChange={(e) => setEmailing({ ...emailing, to: e.target.value })}
                    data-testid="evidence-rule-email-to"
                    placeholder="recipient@example.com"
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-sm focus:border-blue-500 focus:outline-none"
                  />
                </Field>
                <p className="text-[11px] text-slate-500">
                  {emailing.rule_ids.length === 0
                    ? `You'll receive every active evidence rule (${items.filter(r => r.active !== false).length} rules) in one email.`
                    : "Includes the rule label, description, AI coaching instructions, keywords, and required evidence checklist."}
                </p>
              </div>
              <footer className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50">
                <button onClick={() => setEmailing(null)} disabled={busy}
                        className="px-3 py-2 text-xs font-bold uppercase tracking-wider border border-slate-200 rounded-sm hover:bg-white">
                  Cancel
                </button>
                <button onClick={sendEmail} disabled={busy}
                        data-testid="evidence-rule-email-send"
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-wider rounded-sm bg-blue-700 text-white hover:bg-blue-800 disabled:opacity-50">
                  <PaperPlaneTilt size={11} weight="fill" />
                  {busy ? "Sending…" : "Send email"}
                </button>
              </footer>
            </div>
          </div>
        )}

        {/* iter150 — Propose a rule modal (any signed-in user) */}
        {proposing && (
          <div className="fixed inset-0 z-[400] flex items-start justify-center bg-black/40 p-4 pt-12 overflow-y-auto"
               data-testid="evidence-rule-propose-modal" onClick={() => !busy && setProposing(null)}>
            <div className="bg-white border border-slate-200 rounded-md shadow-xl w-full max-w-2xl"
                 onClick={(e) => e.stopPropagation()}>
              <header className="flex items-center gap-2 px-5 py-3 border-b border-emerald-200 bg-emerald-50">
                <HandPalm size={16} weight="fill" className="text-emerald-700" />
                <h2 className="text-sm font-bold tracking-tight">Propose a new evidence rule</h2>
                <button onClick={() => setProposing(null)} disabled={busy}
                        data-testid="evidence-rule-propose-modal-close"
                        className="ml-auto text-slate-400 hover:text-slate-700">
                  <X size={14} weight="bold" />
                </button>
              </header>
              <div className="p-5 space-y-3">
                <p className="text-[11px] text-emerald-900 bg-emerald-50 border border-emerald-200 px-2 py-1.5 rounded-sm">
                  This proposal will be reviewed by a service admin of US Air Compressor / Air Compressor Canada. You'll receive an email when a decision is made.
                </p>
                <Field label="Rule label" hint="What is the defect or scenario? e.g. 'Belt fraying'">
                  <input
                    type="text"
                    value={proposing.label}
                    onChange={(e) => setProposing({ ...proposing, label: e.target.value })}
                    data-testid="evidence-rule-propose-label"
                    placeholder="Belt fraying"
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-sm focus:border-emerald-500 focus:outline-none"
                  />
                </Field>
                <Field label="Suggested category key (optional)" hint="lowercase, snake_case. Admin may override.">
                  <input
                    type="text"
                    value={proposing.category_key}
                    onChange={(e) => setProposing({ ...proposing, category_key: e.target.value })}
                    data-testid="evidence-rule-propose-category"
                    placeholder="belt_fraying"
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-sm focus:border-emerald-500 focus:outline-none"
                  />
                </Field>
                <Field label="Description (optional)">
                  <textarea rows={2} value={proposing.description}
                    onChange={(e) => setProposing({ ...proposing, description: e.target.value })}
                    data-testid="evidence-rule-propose-description"
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-sm focus:border-emerald-500 focus:outline-none resize-y"
                  />
                </Field>
                <Field label="Rationale" hint="Why is this rule needed? What have you seen in the field?">
                  <textarea rows={2} value={proposing.rationale}
                    onChange={(e) => setProposing({ ...proposing, rationale: e.target.value })}
                    data-testid="evidence-rule-propose-rationale"
                    placeholder="We see belt fraying on units after about 1500 hours; users skip taking a video and we end up asking three follow-up emails."
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-sm focus:border-emerald-500 focus:outline-none resize-y"
                  />
                </Field>
                <Field label="Keywords" hint="comma-separated, lowercase">
                  <input
                    type="text"
                    value={(proposing.keywords || []).join(", ")}
                    onChange={(e) => setProposing({ ...proposing, keywords: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                    data-testid="evidence-rule-propose-keywords"
                    placeholder="belt, fraying, worn belt"
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-sm focus:border-emerald-500 focus:outline-none"
                  />
                </Field>
                <Field label="AI coaching" hint="What should the AI agent tell the user?">
                  <textarea rows={3} value={proposing.ai_instructions}
                    onChange={(e) => setProposing({ ...proposing, ai_instructions: e.target.value })}
                    data-testid="evidence-rule-propose-ai-instructions"
                    placeholder="Ask the user to record a 5-second video of the belt while the unit runs at low load."
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-sm focus:border-emerald-500 focus:outline-none resize-y"
                  />
                </Field>
                <Field label="Required evidence">
                  <div className="space-y-2" data-testid="evidence-rule-propose-min-evidence">
                    {(proposing.min_evidence || []).map((ev, i) => (
                      <div key={i} className="flex flex-wrap items-start gap-2 bg-slate-50 border border-slate-200 rounded-sm p-2">
                        <select value={ev.kind}
                          onChange={(e) => {
                            const m = [...proposing.min_evidence];
                            m[i] = { ...m[i], kind: e.target.value };
                            setProposing({ ...proposing, min_evidence: m });
                          }}
                          data-testid={`evidence-rule-propose-kind-${i}`}
                          className="px-2 py-1 text-xs border border-slate-300 rounded-sm">
                          {KIND_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                        </select>
                        <input type="number" min={1} max={10} value={ev.count}
                          onChange={(e) => {
                            const m = [...proposing.min_evidence];
                            m[i] = { ...m[i], count: e.target.value };
                            setProposing({ ...proposing, min_evidence: m });
                          }}
                          data-testid={`evidence-rule-propose-count-${i}`}
                          className="w-14 px-2 py-1 text-xs border border-slate-300 rounded-sm"
                        />
                        <input type="text" value={ev.label} placeholder="Label"
                          onChange={(e) => {
                            const m = [...proposing.min_evidence];
                            m[i] = { ...m[i], label: e.target.value };
                            setProposing({ ...proposing, min_evidence: m });
                          }}
                          data-testid={`evidence-rule-propose-itemlabel-${i}`}
                          className="flex-1 min-w-[180px] px-2 py-1 text-xs border border-slate-300 rounded-sm"
                        />
                        <button type="button"
                          onClick={() => {
                            const m = [...proposing.min_evidence];
                            m.splice(i, 1);
                            setProposing({ ...proposing, min_evidence: m });
                          }}
                          data-testid={`evidence-rule-propose-itemdelete-${i}`}
                          className="px-2 py-1 text-[10px] text-rose-700 hover:bg-rose-50 rounded-sm">
                          <Trash size={11} weight="fill" />
                        </button>
                      </div>
                    ))}
                    <button type="button"
                      onClick={() => setProposing({
                        ...proposing,
                        min_evidence: [...(proposing.min_evidence || []),
                                       { kind: "video", count: 1, label: "", instructions: "" }],
                      })}
                      data-testid="evidence-rule-propose-additem"
                      className="px-2 py-1 text-[11px] font-bold uppercase tracking-wider border border-emerald-300 text-emerald-800 rounded-sm hover:bg-emerald-50">
                      <Plus size={11} weight="bold" className="inline mr-1" /> Add evidence item
                    </button>
                  </div>
                </Field>
              </div>
              <footer className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50">
                <button onClick={() => setProposing(null)} disabled={busy}
                        className="px-3 py-2 text-xs font-bold uppercase tracking-wider border border-slate-200 rounded-sm hover:bg-white">
                  Cancel
                </button>
                <button onClick={submitProposal} disabled={busy}
                        data-testid="evidence-rule-propose-submit"
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-wider rounded-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
                  <PaperPlaneTilt size={11} weight="fill" />
                  {busy ? "Submitting…" : "Submit proposal"}
                </button>
              </footer>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">{label}</span>
      {children}
      {hint && <span className="block text-[10px] text-slate-400 mt-1">{hint}</span>}
    </label>
  );
}
